import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Trash2 } from "lucide-react";
import { Button } from "../../../ui/button";
import { activityStore } from "../../../../store/activity.store";
import CurrentActivitiesList from "../CurrentActivitiesList/CurrentActivitiesList";
import style from "./style.module.css";
import { getActivityTypeOptions } from "./helper";
import EditActivityForm, { ActivityFormRef } from "../EditActivityForm/EditActivityForm";
import { authStore } from "../../../../store/auth.store";
import { Modal } from "../../../ui/modal";
import { TreeSelect } from "../../../ui/tree-select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../ui/alert-dialog";

const AuthoredHeader = () => {
  const { formatMessage } = useIntl();
  const getActivityTypes = activityStore(state => state.getActivityTypes)
  const getActivities = activityStore(state => state.getActivities);
  const startActivity = activityStore(state => state.startActivity);
  const getTagsStartPage = activityStore(state => state.getTagsStartPage);
  const setActivityToEdit = activityStore(state => state.setActivityToEdit);
  const activityToEdit = activityStore(state => state.activityToEdit);
  const deleteActivity = activityStore(state => state.deleteActivity);
  const activityTypes = activityStore(state => state.activityTypes || []);
  const activitiesTypesLoading = activityStore(state => state.loading.getActivityTypes);
  const activitiesLoading = activityStore(state => state.loading.getActivities);
  const tagsLoading = activityStore(state => state.loading.getTagsStartPage);
  const rewriteActivityLoading = activityStore(state => state.loading.rewriteActivity);
  const query = activityStore(state => state.tagsParams.query);
  const settings = authStore(state => state.user?.settings);

  const formRef = useRef<ActivityFormRef>(null);

  const [option, setOption] = useState<string | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    !activitiesTypesLoading && getActivityTypes();
    !activitiesLoading && getActivities();
  }, []);

  useEffect(() => {
    !tagsLoading && getTagsStartPage();
  }, [query]);

  const isSelectedOccurrence = useMemo(() => {
    if (!option) return false;
    return !!activityTypes.find(t => t.id === option)?.occurrence;
  }, [option, activityTypes]);

  const onSelectChangeHandler = (option: string | string[]) => setOption(option as string);
  const startActivityHandler = () => option && startActivity(option)
    .then(() => {
      getActivities();
      setOption(undefined);
    });

  const filterTreeNodeCallback = useCallback((inputValue: string, treeNode: any) => !inputValue
    ? true
    : treeNode.name.toLowerCase().includes(inputValue.toLowerCase()
    ), []);

  const onOkHandler = useCallback(() => {
    formRef.current?.submit();
  }, []);

  const cancelHandler = useCallback(() => setActivityToEdit(null), [setActivityToEdit]);

  const handleDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (activityToEdit) {
      deleteActivity(activityToEdit);
      setActivityToEdit(null);
    }
    setDeleteDialogOpen(false);
  }, [activityToEdit, deleteActivity, setActivityToEdit]);

  return (
    <div className={style.AuthoredHeader}>
      <div className="flex justify-between items-center gap-4 w-full flex-wrap">
        <div className={style.HeaderActions}>
          <TreeSelect
            className={style.select}
            filterTreeNode={filterTreeNodeCallback}
            loading={activitiesTypesLoading}
            showSearch
            onChange={onSelectChangeHandler}
            value={option}
            treeData={getActivityTypeOptions(activityTypes)}
            placeholder={formatMessage({ id: 'SELECT_ACTIVITY' })}
          />
          <Button
            variant="segment"
            onClick={startActivityHandler}
          >
            {formatMessage({ id: isSelectedOccurrence ? 'LOG_ACTIVITY' : 'START_ACTIVITY' })}
          </Button>
        </div>
        <div className="text-right">
          <CurrentActivitiesList settings={settings} />
        </div>
      </div>
      <Modal
        title={formatMessage({ id: 'EDIT_ACTIVITY' })}
        open={!!activityToEdit}
        onCancel={cancelHandler}
        footer={
          <div className="flex justify-between w-full">
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleDeleteClick}
            >
              <Trash2 className="h-4 w-4" />
              {formatMessage({ id: 'DELETE' })}
            </Button>
            <div className="flex gap-2">
              <Button variant="link" onClick={cancelHandler}>
                {formatMessage({ id: 'CANCEL' })}
              </Button>
              <Button
                variant="segment"
                onClick={onOkHandler}
                loading={rewriteActivityLoading}
              >
                {formatMessage({ id: 'SAVE' })}
              </Button>
            </div>
          </div>
        }
      >
        {!!activityToEdit && <EditActivityForm ref={formRef} activity={activityToEdit} />}
      </Modal>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{formatMessage({ id: 'DELETE' })}</AlertDialogTitle>
            <AlertDialogDescription>
              {formatMessage({ id: 'DELETE_ACTIVITY_CONFIRM' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{formatMessage({ id: 'CANCEL' })}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {formatMessage({ id: 'DELETE' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default AuthoredHeader;
